/* eslint-disable no-restricted-globals */
import React, { useState, useRef, LegacyRef, ChangeEvent, useCallback, useEffect } from "react";

import { MenuItem, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Category,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
// import { FiDownloadCloud } from "react-icons/fi";
import { AiOutlineDown } from "react-icons/ai";
import { HiOutlineMail } from "react-icons/hi";
import { useHistory } from "react-router-dom";

import BookingView, { BookingViewHandles } from "../../../../components/Booking/View";
import { Color } from "../../../../components/Form/Button";
import { StyledMenu } from "../../../../components/Generic/AnchorMenu";
import BadgeShipmentTrackingStatus from "../../../../components/Generic/BadgeShipmentTrackingStatus";
import { renderMilestoneDateByTypeAndReference } from "../../../../components/Generic/MilestoneDates";
import { NotificationToast } from "../../../../components/Generic/Toast/NotificationToast";
import { DefaultTooltip } from "../../../../components/Generic/Tooltip";
import { MoreOptions } from "../../../../components/MoreOptionsComponent/MoreOptions";
import TitlePage from "../../../../components/TitlePage";
import { PRIVATE_ROUTES } from "../../../../constants";
import { useAppState } from "../../../../context/StateContext";
import useActionShipment from "../../../../hooks/useActionShipment";
import { Customer } from "../../../../models/customer";
import { Job } from "../../../../models/job";
import { MilestoneReference, MilestoneType, MilestoneValue } from "../../../../models/milestone";
import { Project } from "../../../../models/project";
import { Filters, Searches } from "../../../../models/shipeezi.table";
import { ShipmentTracking } from "../../../../models/shipment.tracking";
import { Shipment } from "../../../../models/shipments";
import ProjectService from "../../../../services/project.service";
import { capitalizeFirstLetter } from "../../../../util/capitalizeFirstLetter";
import { ShipmentStatus, UsedInEmailTemplates } from "../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../util/Format";
import BookingBasedModal, { BookingBasedHandles } from "./components/BookingBasedModal";
import DelayedBadge from "./components/DelayedBadge";
import SharedBooking from "./components/SharedBooking";
import SendCommunicationModal, {
  SendCommunicationModalHandle,
} from "../../../../components/Generic/SendCommunicationModal";
import ShipmentCommunicationModal, {
  IShipmentCommunicationModal,
} from "./components/ShipmentCommunicationModal";
import ShipmentBadgeStatus from "./components/StatusBadge";
import RenderContainer from "./components/TrackableContainer";
import getShipmentOptions from "./getShipmentOptions";
import useStyles, { ExportIcon, ImportIcon, MailIcon, NoWrapButton } from "./styles";
import ShipmentSideView, { ShipmentViewHandles } from "./View";

/* Maximum allowed size in bytes 8MB */
const MAX_ALLOWED_SIZE = 8 * 1024 * 1024;
const FILE_TYPE_ALLOWED = "text/csv";

const Shipments = () => {
  const { currentUser, isFMVersion, isSPVersion } = useAppState();
  const { t: translate } = useTranslation();
  const communicationModalRef = useRef<IShipmentCommunicationModal>(null);

  const { typeOptions, statusOptions, containerTypeOptions } = getShipmentOptions();

  const { getAllShipments, getAllShipmentsCount, useImportShipment, useExportShipment } =
    useActionShipment();

  const classes = useStyles();
  const history = useHistory();

  const sideViewRef = useRef<ShipmentViewHandles>(null);

  const [shipments, setShipments] = useState<Shipment[]>([] as Shipment[]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [search, setSearch] = useState<Searches>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchFilters, setSearchFilters] = useState<Filters>();

  const hiddenFileInput: LegacyRef<HTMLInputElement> = useRef(null);
  const bookingBasedRef: React.RefObject<BookingBasedHandles> = useRef<BookingBasedHandles>(null);
  const bookingViewRef = useRef<BookingViewHandles>(null);

  const {
    data: shipmentsData,
    isFetching: loadingShipments,
    isRefetching: refetchingShipments,
  } = getAllShipments(page, pageSize, search, searchFilters);
  const {
    data: totalItemsData,
    isFetching: loadingShipmentsCount,
    isRefetching: refetchingShipmentsCount,
  } = getAllShipmentsCount(search, searchFilters);

  useEffect(() => {
    if (shipmentsData) {
      setShipments(shipmentsData);
    }

    setTotalItems(totalItemsData || 0);
  }, [shipmentsData, totalItemsData]);

  const { mutate: importShipment } = useImportShipment();
  const { mutate: exportShipment } = useExportShipment();

  const { data: projects } = useQuery(
    ["projects.get.combobox"],
    () =>
      ProjectService.getAllNetwork({
        attributes: ["id", "name", "companyId"],
        companyId: currentUser?.companyId,
        group: ["projects.id"],
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: isFMVersion,
      refetchOnWindowFocus: false,
    }
  );

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setSearchFilters(value);
    setPage(0);
  };

  const handleSearch = (value: Searches) => {
    setSearch(value);
    setPage(0);
  };

  const handleImport = () => {
    if (hiddenFileInput && hiddenFileInput?.current) hiddenFileInput?.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const file = event?.target?.files[0];

      if (file.type && file.type !== FILE_TYPE_ALLOWED) {
        NotificationToast.warning(translate("thisFileExtensionIsNotAllowed"));

        return;
      }

      if (file?.size && file?.size > MAX_ALLOWED_SIZE) {
        NotificationToast.warning(translate("fileIsTooBigMax8Mb"));

        return;
      }

      if (file) {
        const data = new FormData();

        data.append("file", file, file.name);

        importShipment({
          file: data,
          userId: currentUser?.id.toString() || "",
        });
      }
    }
    event.target.value = "";
  };

  const handleExport = () => {
    if (selectedItems.length < 1) {
      NotificationToast.warning(translate("selectAtLeastOneItem"));

      return;
    }
    selectedItems.forEach((selectedItem) => {
      exportShipment(selectedItem);
    });
  };
  const handleEmailComposer = () => {
    if (selectedItems.length < 1) {
      NotificationToast.warning(translate("selectAtLeastOneItem"));

      return;
    }

    const firstShipmentId = selectedItems[0];
    const shipment = shipments.find((s) => s.id === firstShipmentId);

    if (!shipment) {
      NotificationToast.warning(translate("shipmentNotFound"));
      return;
    }

    const customer = shipment.consignee;

    communicationModalRef.current?.open(selectedItems);
  };

  const renderShipmentType = (shipmentType: string) => {
    const labelClass: string =
      shipmentType?.toLocaleLowerCase() === "air" ? classes.airTypeLabel : classes.seaTypeLabel;

    return <span className={labelClass}>{capitalizeFirstLetter(shipmentType)}</span>;
  };

  const renderSendEmailButton = (id: string | number, row: Shipment) => {
    const onClickEvent = () => {
      const customer = row.consignee;

      communicationModalRef.current?.open([Number(id)]);
    };

    return (
      <Tooltip data-cy="action-communication" title={translate("communication")} placement="top">
        <div onClick={onClickEvent} className={classes.button}>
          <HiOutlineMail
            size={18}
            className={row?.isEmailSent ? classes.emailSent : classes.emailNotSent}
          />
        </div>
      </Tooltip>
    );
  };

  const openBookingView = (bookingId?: number) => {
    if (bookingId) bookingViewRef?.current?.open(bookingId);
  };

  const columnsFM: Column[] = [
    {
      name: "consignee",
      title: translate("consignee"),
      formatText: (consignee?: Customer) => consignee?.name || "",
      hideSearch: true,
      hideShowColumn: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "eventType",
      title: translate("project"),
      hideSearch: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "shipmentType",
      title: translate("mode"),
      hideSearch: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "bookingNo",
      title: `${translate("booking")} #`,
      formatComponent: (value: string, row: Shipment) => (
        <SharedBooking
          openBookingModal={openBookingView}
          bookingId={row?.bookingId}
          bookingReference={value}
        />
      ),
      searchIdentifier: "booking_no",
      style: { paddingRight: "8px", whiteSpace: "nowrap" },
    },
    {
      name: "billOfLanding",
      title: translate("billOfLanding"),
      hideSearch: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "containerId",
      title: `${translate("shipment")} #`,
      searchIdentifier: "container_id",
      hideShowColumn: true,
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "containerNumbers",
      title: `${translate("container")} #`,
      formatComponent: (value: string, row) => (
        <RenderContainer
          container={value}
          marineTrafficId={row?.marineTrafficId}
          isEmptyState={
            row?.shipmentTracking?.shipmentStatus === ShipmentStatus.TRACKING_NOT_AVAILABLE ||
            row?.shipmentTracking?.shipmentStatus === ShipmentStatus.WAITING_FOR_UPDATE
          }
        />
      ),
      searchIdentifier: "container_numbers",
      style: { whiteSpace: "nowrap" },
    },
    {
      name: "poNumbers",
      title: translate("orders"),
      formatComponent: (value) => (
        <MoreOptions
          options={value?.split("/")}
          title={translate("orders")}
          manyOptionsToShow={1}
        ></MoreOptions>
      ),
      searchIdentifier: "po_numbers",
    },
    {
      name: "totalQuantity",
      title: translate("cartonQuantity"),
      searchIdentifier: "total_quantity",
    },
    { name: "origin", title: translate("origin"), style: { minWidth: "120px" } },
    { name: "destination", title: translate("destination"), style: { minWidth: "120px" } },
    {
      name: "milestones",
      title: translate("etdOrigin"),
      formatComponent: (milestones: MilestoneValue[], row: Shipment) => {
        if (row?.shipmentTracking && row?.shipmentTracking?.originPortActualDeparture)
          return (
            <span>
              {formatDateWithoutTimeZone(row?.shipmentTracking?.originPortActualDeparture)}
            </span>
          );

        return renderMilestoneDateByTypeAndReference(
          milestones,
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.ORIGIN
        );
      },
      formatText: (value: ShipmentTracking) =>
        formatDateWithoutTimeZone(value?.originPortActualDeparture),
      hideSearch: true,
    },
    {
      name: "milestones",
      title: translate("etaDestination"),
      formatComponent: (milestones: MilestoneValue[], row: Shipment) => {
        if (row?.shipmentTracking && row?.shipmentTracking?.finalPortPredictiveArrivalLast)
          return (
            <span>
              {formatDateWithoutTimeZone(row?.shipmentTracking?.finalPortPredictiveArrivalLast)}
            </span>
          );

        return renderMilestoneDateByTypeAndReference(
          milestones,
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        );
      },
      hideSearch: true,
    },
    {
      name: "milestones",
      title: translate("arrivalAtDestination"),
      formatComponent: (milestones: MilestoneValue[], row: Shipment) => {
        if (row?.shipmentTracking && row?.shipmentTracking?.finalPortActualArrival) {
          return (
            <span>{formatDateWithoutTimeZone(row?.shipmentTracking?.finalPortActualArrival)}</span>
          );
        }

        return renderMilestoneDateByTypeAndReference(
          milestones,
          MilestoneType.ACTUAL,
          MilestoneReference.SHIPMENT.ACTUAL.ARRIVAL_AT_DESTINATION
        );
      },
      hideSearch: true,
    },
    {
      name: "shipmentTracking",
      title: translate("delayed"),
      formatComponent: (shipmentTracking: ShipmentTracking) => (
        <DelayedBadge shipmentTracking={shipmentTracking} />
      ),
      hideSearch: true,
    },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string, row: Shipment) => {
        if (row.shipmentTrackingId)
          return (
            <BadgeShipmentTrackingStatus
              className={classes.badge}
              value={row.shipmentTracking?.shipmentStatus ?? ""}
            />
          );

        return status && <ShipmentBadgeStatus className={classes.badge} value={status} />;
      },
      style: { paddingLeft: "12px" },
      hideSearch: true,
      stickyRight: 116,
    },
  ];

  const columnsSP: Column[] = [
    { name: "containerId", title: `${translate("shipment")} #`, searchIdentifier: "container_id" },
    {
      name: "shipmentType",
      title: translate("type"),
      formatComponent: (value: string) => value && renderShipmentType(value),
      hideSearch: true,
    },
    {
      name: "containerNumbers",
      title: `${translate("container")} #`,
      searchIdentifier: "container_numbers",
    },
    { name: "poNumbers", title: translate("orders"), hideSearch: true },
    {
      name: "totalQuantity",
      title: translate("ssccQty"),
      searchIdentifier: "total_quantity",
      hideSearch: true,
    },
    {
      name: "milestones",
      title: translate("etdAtOrigin"),
      formatComponent: (milestoneValues: MilestoneValue[]) =>
        renderMilestoneDateByTypeAndReference(
          milestoneValues,
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.ORIGIN
        ),
      hideSearch: true,
    },
    { name: "origin", title: translate("origin") },
    {
      name: "milestones",
      title: translate("etaAtDestination"),
      formatComponent: (milestoneValues: MilestoneValue[]) =>
        renderMilestoneDateByTypeAndReference(
          milestoneValues,
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        ),
      hideSearch: true,
    },
    { name: "destination", title: translate("destination") },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string) => (
        <BadgeShipmentTrackingStatus value={status ?? translate("waitingForUpdate")} />
      ),
      hideSearch: true,
    },
  ];

  const editShipment = (id: number | string) => {
    history.push({
      pathname: `${PRIVATE_ROUTES.SHIPMENTS_MNT}/${id}`,
      state: { viewOnly: false },
    });
  };

  const viewShipment = (id: number | string) => {
    if (id && !Number.isNaN(Number(id))) sideViewRef.current?.open(Number(id));
  };

  const filterCategories: Category[] = [
    {
      name: translate("type"),
      column: "shipment_type",
      options: typeOptions,
    },
    {
      name: translate("status"),
      column: "status_shipment",
      options: statusOptions,
    },
    ...(isFMVersion
      ? [
          {
            name: translate("projectName"),
            column: "event_type",
            options: projects?.data?.rows?.map((project: Project) => project?.name),
          },
          {
            name: translate("containerType"),
            column: "container_type",
            options: containerTypeOptions,
          },
        ]
      : []),
  ];
  const openCreateShipment = (shipment?: Shipment) => {
    history.push({
      pathname: `${PRIVATE_ROUTES.SHIPMENTS_MNT}`,
      state: { viewOnly: false, ...(shipment ? { shipment } : {}) },
    });
  };

  const renderImportShipment = useCallback(() => {
    return; // remove for now

    return (
      <DefaultTooltip title={translate("import")} placement="top">
        <div>
          <NoWrapButton customColor={Color.SECONDARY_OUTLINED} onClick={handleImport} disabled>
            <ImportIcon size={16} />
            {translate("import")}
          </NoWrapButton>
        </div>
      </DefaultTooltip>
    );
  }, []);

  const renderNewShipment = useCallback(() => {
    const open: boolean = !!anchorEl;

    return (
      <DefaultTooltip title={translate("addNewShipment")} placement="top">
        <div>
          <NoWrapButton
            customColor={Color.PRIMARY}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            endIcon={<AiOutlineDown size={16} />}
          >
            {translate("newShipment")}
          </NoWrapButton>
          <StyledMenu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            className={classes.menu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => onOpenBookingBasedModal()}>
              {translate("basedOnABooking")}
            </MenuItem>
            <MenuItem onClick={() => openCreateShipment()}>{translate("manual")}</MenuItem>
          </StyledMenu>
        </div>
      </DefaultTooltip>
    );
  }, [anchorEl]);

  const renderExportShipment = useCallback(() => {
    return (
      <DefaultTooltip title={translate("export")} placement="top">
        <div>
          <NoWrapButton customColor={Color.SECONDARY_OUTLINED} onClick={handleExport}>
            <ExportIcon size={16} />
            {translate("export")}
          </NoWrapButton>
        </div>
      </DefaultTooltip>
    );
  }, [selectedItems]);

  const renderSendEmail = useCallback(() => {
    return (
      <DefaultTooltip title={translate("sendEmail")} placement="top">
        <div>
          <NoWrapButton customColor={Color.SECONDARY_OUTLINED} onClick={handleEmailComposer}>
            <MailIcon size={16} />
            {translate("sendEmail")}
          </NoWrapButton>
        </div>
      </DefaultTooltip>
    );
  }, [selectedItems]);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: !(selectedItems?.length >= 1)
        ? ["onImportShipment"]
        : ["onSendEmail", "onExportShipment"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleNewRecord: {
      text: translate("create"),
      action: openCreateShipment,
      tooltip: translate("createShipment"),
    },
    onNewShipment: renderNewShipment(),
    onSendEmail: renderSendEmail(),
    onExportShipment: renderExportShipment(),
    onImportShipment: renderImportShipment(),
  };
  // TODO: create download service
  // const renderDownloadShipmentInformation = (id?: number | string) => {
  //   return (
  //     <Tooltip title={translate("downloadShipmentInformations")} placement="top">
  //       <div className={classes.button}>
  //         <FiDownloadCloud color="#475467" size={18} />
  //       </div>
  //     </Tooltip>
  //   );
  // };

  const onOpenBookingBasedModal = () => {
    bookingBasedRef?.current?.open();
  };

  return (
    <div data-cy="shipments-grid">
      <TitlePage title={translate("shipments")} />
      <div className={classes.shipmentsGridWrapper}>
        <ShipeeziNewGrid
          stickyActions
          data={shipments}
          totalItems={totalItems}
          loadingData={
            loadingShipments ||
            refetchingShipments ||
            loadingShipmentsCount ||
            refetchingShipmentsCount
          }
          namePage={translate("shipments")}
          emptyData={
            loadingShipments ? translate("loadingShipments") : translate("noInformationToBeShown")
          }
          columns={isSPVersion ? columnsSP : columnsFM}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [20, 40, 60, 100] : []}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          rowActions={{
            orderBy: ["handleEdit", "handleDetails", "sendEmail"],
            handleEdit: (rowId) => editShipment(rowId),
            handleDetails: (rowId) => viewShipment(rowId),
          }}
          rowActionsExtra={[
            {
              title: translate("sendEmail"),
              key: "sendEmail",
              buttonAndAction: renderSendEmailButton,
            },
            // {
            //   title: translate("shipmentInformation"),
            //   key: "downloadShipmentInformation",
            //   buttonAndAction: renderDownloadShipmentInformation,
            // },
          ]}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showSelection={totalItems !== 0}
          showFooterGrid={!!totalItems}
        />
      </div>

      {/* <SendCommunicationModal
        ref={communicationModalRef}
        usedIn={UsedInEmailTemplates.SHIPMENTS}
      />*/}
      <ShipmentCommunicationModal ref={communicationModalRef} />
      <ShipmentSideView ref={sideViewRef} />

      <input
        type="file"
        ref={hiddenFileInput}
        data-cy={`ship-import-input-file`}
        onChange={handleFileChange}
        className={classes.inputFile}
        accept=".csv"
      />
      <BookingBasedModal
        ref={bookingBasedRef}
        onGetBooking={(shipment: Shipment) => openCreateShipment(shipment)}
        onCancel={() => setAnchorEl(null)}
      />
      <BookingView ref={bookingViewRef} />
    </div>
  );
};

export default Shipments;
